import {
  Application,
  Container,
  Graphics,
  RenderLayer,
  Text,
} from "pixi.js";
import type { MapDocument, RoadFeature, BuildingFeature } from "@maps/schema";

const COLORS = {
  roadFill: 0x3a3a3a,
  roadEdge: 0x2a2a2a,
  roadCenterline: 0xffcc00,
  buildingFill: 0xb5a58a,
  buildingEdge: 0x8a7a5a,
  buildingSelected: 0xff8800,
  vertex: 0x4488ff,
  vertexHover: 0xff4488,
  background: 0x1a1a22,
  grid: 0x2a2a35,
} as const;

export class EditorScene {
  app: Application;
  stage: Container;
  worldLayer: Container;
  roadsLayer: Container;
  buildingsLayer: Container;
  overlayLayer: Container;
  gridGfx: Graphics;
  backgroundGfx: Graphics;
  private initialized = false;

  constructor() {
    this.app = new Application();
    this.stage = new Container();
    this.worldLayer = new Container();
    this.roadsLayer = new Container();
    this.buildingsLayer = new Container();
    this.overlayLayer = new Container();
    this.gridGfx = new Graphics();
    this.backgroundGfx = new Graphics();
  }

  async init(canvas: HTMLCanvasElement): Promise<void> {
    await this.app.init({
      canvas,
      background: COLORS.background,
      antialias: true,
      autoDensity: true,
      resolution: window.devicePixelRatio,
      resizeTo: canvas.parentElement ?? window,
    });

    this.stage.addChild(this.worldLayer);
    this.worldLayer.addChild(this.gridGfx);
    this.worldLayer.addChild(this.buildingsLayer);
    this.worldLayer.addChild(this.roadsLayer);
    this.worldLayer.addChild(this.overlayLayer);
    this.app.stage.addChild(this.stage);
    this.initialized = true;
  }

  destroy(): void {
    if (!this.initialized) return;
    this.app.destroy(true);
  }

  get ready(): boolean {
    return this.initialized;
  }

  get canvasWidth(): number {
    return this.app.screen.width;
  }

  get canvasHeight(): number {
    return this.app.screen.height;
  }

  renderGrid(cam: { cx: number; cy: number; zoom: number }): void {
    const g = this.gridGfx;
    g.clear();

    const step = 50; // world meters
    const viewW = this.canvasWidth / cam.zoom;
    const viewH = this.canvasHeight / cam.zoom;
    const left = cam.cx - viewW / 2;
    const right = cam.cx + viewW / 2;
    const top = cam.cy - viewH / 2;
    const bottom = cam.cy + viewH / 2;

    const startX = Math.floor(left / step) * step;
    const endX = Math.ceil(right / step) * step;
    const startY = Math.floor(top / step) * step;
    const endY = Math.ceil(bottom / step) * step;

    g.setStrokeStyle({ width: 1 / cam.zoom, color: COLORS.grid });
    for (let x = startX; x <= endX; x += step) {
      g.moveTo(x, top);
      g.lineTo(x, bottom);
    }
    for (let y = startY; y <= endY; y += step) {
      g.moveTo(left, y);
      g.lineTo(right, y);
    }
    g.stroke();
  }

  renderDoc(doc: MapDocument, selectedId: string | null): void {
    this.roadsLayer.removeChildren();
    this.buildingsLayer.removeChildren();

    for (const feature of doc.features) {
      if (feature.type === "road") {
        this.drawRoad(feature, selectedId === feature.id);
      } else if (feature.type === "building") {
        this.drawBuilding(feature, selectedId === feature.id);
      }
    }
  }

  private drawRoad(road: RoadFeature, selected: boolean): void {
    const g = new Graphics();
    const halfW = road.width / 2;
    const cl = road.centerline;

    // Road body (filled polygon from centerline + width)
    if (cl.length >= 2) {
      const { left, right } = offsetPolyline(cl, halfW);
      g.moveTo(left[0]!.x, left[0]!.y);
      for (let i = 1; i < left.length; i++) {
        g.lineTo(left[i]!.x, left[i]!.y);
      }
      for (let i = right.length - 1; i >= 0; i--) {
        g.lineTo(right[i]!.x, right[i]!.y);
      }
      g.closePath();
      g.fill({ color: COLORS.roadFill });
      g.setStrokeStyle({ width: 0.5, color: COLORS.roadEdge });
      g.stroke();
    }

    // Centerline (dashed yellow)
    g.setStrokeStyle({
      width: 1,
      color: COLORS.roadCenterline,
      alpha: 0.8,
    });
    for (let i = 0; i < cl.length - 1; i++) {
      g.moveTo(cl[i]!.x, cl[i]!.y);
      g.lineTo(cl[i + 1]!.x, cl[i + 1]!.y);
    }
    g.stroke();

    if (selected) {
      // Highlight vertices
      for (const v of cl) {
        g.circle(v.x, v.y, 3);
        g.fill({ color: COLORS.vertexHover });
      }
    }

    this.roadsLayer.addChild(g);
  }

  private drawBuilding(b: BuildingFeature, selected: boolean): void {
    const g = new Graphics();
    const poly = b.polygon;

    g.moveTo(poly[0]!.x, poly[0]!.y);
    for (let i = 1; i < poly.length; i++) {
      g.lineTo(poly[i]!.x, poly[i]!.y);
    }
    g.closePath();
    g.fill({ color: selected ? COLORS.buildingSelected : COLORS.buildingFill });
    g.setStrokeStyle({ width: 1, color: COLORS.buildingEdge });
    g.stroke();

    // Height indicator
    const cx = poly.reduce((s, p) => s + p.x, 0) / poly.length;
    const cy = poly.reduce((s, p) => s + p.y, 0) / poly.length;
    const label = new Text({
      text: `${b.height}m`,
      style: { fontSize: 10, fill: 0xffffff, align: "center" },
    });
    label.anchor.set(0.5);
    label.x = cx;
    label.y = cy;
    g.addChild(label);

    if (selected) {
      for (const v of poly) {
        g.circle(v.x, v.y, 3);
        g.fill({ color: COLORS.vertexHover });
      }
    }

    this.buildingsLayer.addChild(g);
  }

  /** Draw in-progress tool preview (vertices being placed). */
  renderOverlay(
    vertices: { x: number; y: number }[],
    cursor: { x: number; y: number } | null,
    snapTarget: { x: number; y: number } | null,
  ): void {
    const g = this.overlayLayer;
    g.removeChildren();

    if (vertices.length === 0 && !cursor) return;

    const gfx = new Graphics();

    if (vertices.length >= 1) {
      // Existing vertices + lines
      gfx.setStrokeStyle({ width: 2, color: COLORS.vertex, alpha: 0.8 });
      gfx.moveTo(vertices[0]!.x, vertices[0]!.y);
      for (let i = 1; i < vertices.length; i++) {
        gfx.lineTo(vertices[i]!.x, vertices[i]!.y);
      }
      // Line to cursor
      const end = snapTarget ?? cursor;
      if (end) {
        gfx.lineTo(end.x, end.y);
      }
      gfx.stroke();

      // Vertex dots
      for (const v of vertices) {
        gfx.circle(v.x, v.y, 3);
        gfx.fill({ color: COLORS.vertex });
      }
    }

    // Snap indicator
    if (snapTarget) {
      gfx.circle(snapTarget.x, snapTarget.y, 5);
      gfx.setStrokeStyle({ width: 2, color: COLORS.vertexHover });
      gfx.stroke();
    }

    g.addChild(gfx);
  }

  clearOverlay(): void {
    this.overlayLayer.removeChildren();
  }
}

/** Compute left/right offset edges of a polyline at distance d. */
function offsetPolyline(
  points: { x: number; y: number }[],
  d: number,
): { left: { x: number; y: number }[]; right: { x: number; y: number }[] } {
  const left: { x: number; y: number }[] = [];
  const right: { x: number; y: number }[] = [];

  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    let dx: number, dy: number;
    if (i === 0) {
      dx = points[1]!.x - p.x;
      dy = points[1]!.y - p.y;
    } else if (i === points.length - 1) {
      dx = p.x - points[i - 1]!.x;
      dy = p.y - points[i - 1]!.y;
    } else {
      // Average of prev and next direction
      const d1x = p.x - points[i - 1]!.x;
      const d1y = p.y - points[i - 1]!.y;
      const d2x = points[i + 1]!.x - p.x;
      const d2y = points[i + 1]!.y - p.y;
      dx = d1x + d2x;
      dy = d1y + d2y;
    }
    const len = Math.hypot(dx, dy) || 1;
    // Perpendicular (rotate 90° CCW = left, CW = right)
    const nx = -dy / len;
    const ny = dx / len;
    left.push({ x: p.x + nx * d, y: p.y + ny * d });
    right.push({ x: p.x - nx * d, y: p.y - ny * d });
  }

  return { left, right };
}

void RenderLayer;